import { DatePipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';

import { Store } from '@ngrx/store';

import { MatIcon } from '@angular/material/icon';

import { ChatStateService } from '@lib/chat/services/chat-state.service';
import { ChatThread, ThreadType } from '@lib/chat/models/chat.interface';
import { selectChatDraftsMap } from '@lib/chat/store/chat.selectors';

import { AssigneePickerComponent } from '@lib/shared-ui/commons/assignee-picker/assignee-picker.component';

@Component({
  selector: 'chat-thread',
  standalone: true,
  imports: [DatePipe, NgClass, MatIcon, AssigneePickerComponent],
  templateUrl: './chat-thread.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatThreadComponent {
  private readonly store = inject(Store);
  private readonly chatState = inject(ChatStateService);

  readonly selected = input.required<boolean>();
  readonly isOdd = input.required<boolean>();
  readonly unreadCount = input.required<number>();
  readonly thread = input.required<ChatThread>();
  readonly ownUserId = input.required<string>();
  readonly threadAssignees = input.required<{ id: string; name: string }[]>();
  readonly canModifyAssignee = input<boolean>(false);

  protected readonly useAssigneePicker = computed<boolean>(() => this.chatState.features().showThreadTabs);

  protected readonly hasDraft = computed(() => {
    const draftsMap = this.store.selectSignal(selectChatDraftsMap)();

    return draftsMap.has(this.thread().id);
  });

  protected readonly currentAssignee = computed((): { id: string; name: string } | null => {
    if (this.thread().assignees && this.thread().assignees.length > 0) {
      const firstAssigneeId = this.thread().assignees[0];
      const assignee = this.threadAssignees().find(item => item.id === firstAssigneeId);

      return assignee ? { id: assignee.id, name: assignee.name } : null;
    } else return null;
  });

  protected readonly threadBorderClass = computed(() => {
    if (this.currentAssignee() && this.currentAssignee().id === this.ownUserId()) return 'border-2 border-green-success';
    else if (!this.currentAssignee() && this.unreadCount() > 0) return 'border-2 border-red-error-dark';
    else return '';
  });

  getThreadTypeLabel(threadType: ThreadType) {
    switch (threadType) {
      case ThreadType.HealthManagerChannel:
        return 'HM';
      case ThreadType.SupportChannel:
        return 'Support';
      case ThreadType.DoctorChannel:
        return 'Doctor';
      case ThreadType.AiAssistant:
        return 'AI';
      default:
        return '';
    }
  }

  getThreadTypeLabelColor(threadType: ThreadType, isSelected: boolean) {
    switch (threadType) {
      case ThreadType.SupportChannel:
        return isSelected ? 'bg-gray-1' : 'bg-yellow-700';
      case ThreadType.HealthManagerChannel:
        return isSelected ? 'bg-gray-1' : 'bg-success-800';
      case ThreadType.DoctorChannel:
        return isSelected ? 'bg-gray-1' : 'bg-primary-500';
      case ThreadType.AiAssistant:
        return isSelected ? 'bg-gray-1' : 'bg-purple-800';
      default:
        return isSelected ? 'bg-gray-1' : 'bg-gray-800';
    }
  }

  protected onAssigneeChanged(assignee: { id: string; name: string } | null): void {
    const threadId = this.thread().id;

    if (assignee) this.chatState.assignThread(threadId, assignee.id).subscribe();
    else this.chatState.unassignThread(threadId).subscribe();
  }
}
